import {
  ConsumptionType,
  FulfillmentType,
  OrderStatus,
  PaymentStatus,
  type Order,
  type OrderItem,
  type ProductResponseDto,
  type SubOrder,
} from "@/api/api-client"

export const BUYER_ORDER_PIPELINE_STEPS = (statusLabels: Partial<Record<string, string>>) => [
  { index: 0, label: statusLabels[OrderStatus.Pending] || "Pendiente" },
  { index: 1, label: statusLabels[OrderStatus.StockVerified] || "Verificado" },
  { index: 2, label: statusLabels[OrderStatus.ShippingQuoted] || "Cotizado" },
  { index: 3, label: statusLabels[OrderStatus.PaymentReceived] || "Pagado" },
  { index: 4, label: statusLabels[OrderStatus.OrderShipped] || "En camino" },
]

export function buildCartProductFromOrderItem(item: OrderItem): ProductResponseDto | null {
  const product = item.product
  const productId = item.productId ?? product?.id

  if (!productId) return null

  const images =
    product?.images?.map((image) => ({
      id: image.id ?? 0,
      fileUrl: image.fileUrl,
      isPrimary: image.isPrimary ?? false,
    })) ?? []

  return {
    id: productId,
    name: product?.name ?? item.productName,
    sku: product?.sku ?? item.sku,
    description: product?.description,
    price: item.price ?? product?.price ?? 0,
    unitCost: item.unitCost ?? product?.unitCost ?? 0,
    weight: product?.weight ?? 1,
    weightUnit: product?.weightUnit ?? item.weightUnit ?? "kg",
    stockQuantity: product?.stockQuantity ?? 0,
    lowStockThreshold: product?.lowStockThreshold ?? 0,
    leadTimeDays: product?.leadTimeDays ?? 0,
    safetyStockLevel: product?.safetyStockLevel ?? 0,
    avgDailySales: 0,
    reorderPoint: 0,
    isTaxable: item.isTaxable ?? product?.isTaxable ?? false,
    vendorId: product?.vendorId ?? "",
    categoryId: product?.categoryId,
    categoryName: product?.category?.nameEs,
    providerId: product?.providerId ?? item.providerId,
    providerName: product?.provider?.name ?? item.providerName,
    categoryIds: product?.categoryHierarchyIds ?? [],
    primaryImageUrl: images.find((image) => image.isPrimary)?.fileUrl ?? images[0]?.fileUrl,
    imageUrls: images.map((image) => image.fileUrl),
    images,
    technicalSpecificationUrl: product?.technicalSpecification?.fileUrl,
    consumptionType: product?.consumptionType ?? ConsumptionType.Industrial,
  }
}

export function getDeliveredAt(order: Order) {
  if (order.status !== OrderStatus.OrderComplete) return null

  const completedSubOrderDates = order.subOrders
    ?.filter((subOrder) => subOrder.status === OrderStatus.OrderComplete && subOrder.updatedAt)
    .map((subOrder) => new Date(subOrder.updatedAt!))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((a, b) => b.getTime() - a.getTime())

  if (completedSubOrderDates?.length) return completedSubOrderDates[0]
  return order.orderDate ? new Date(order.orderDate) : null
}

export function getBuyerOrderItemsCount(order: Order) {
  return order.subOrders?.reduce((sum, subOrder) => sum + (subOrder.items?.length || 0), 0) || 0
}

export function getBuyerOrderTotalAmount(order: Order) {
  return (
    order.subOrders?.reduce((sum, subOrder) => sum + (subOrder.totalAmount ?? 0), 0) ||
    order.totalAmount ||
    0
  )
}

export function canReorderOrder(order: Order) {
  return (
    order.status === OrderStatus.OrderComplete ||
    order.subOrders?.some((subOrder) => subOrder.status === OrderStatus.OrderComplete) ||
    false
  )
}

export function getShippingQuoteSubOrders(subOrders?: SubOrder[]) {
  return subOrders?.filter((subOrder) => subOrder.status === OrderStatus.ShippingQuoted) ?? []
}

export function getLatestPaymentStatus(subOrder: SubOrder) {
  if (subOrder.latestPaymentStatus) return subOrder.latestPaymentStatus

  return subOrder.payments
    ?.slice()
    .sort((a, b) => {
      const left = a.createdAt ? new Date(a.createdAt).getTime() : 0
      const right = b.createdAt ? new Date(b.createdAt).getTime() : 0
      return right - left
    })[0]?.status
}

export function isPaymentAttemptProcessing(subOrder: SubOrder) {
  return getLatestPaymentStatus(subOrder) === PaymentStatus.Processing
}

export function getOrderReorderItems(order: Order) {
  const subOrderLineItems =
    order.subOrders?.flatMap(
      (subOrder) =>
        subOrder.items?.map((item) => ({
          item,
          pickupWarehouseId:
            subOrder.fulfillmentType === FulfillmentType.Pickup
              ? subOrder.pickupWarehouseId
              : undefined,
        })) ?? []
    ) ?? []
  const masterOrderLineItems =
    order.items?.map((item) => ({ item, pickupWarehouseId: undefined })) ?? []

  return subOrderLineItems.length > 0 ? subOrderLineItems : masterOrderLineItems
}
